/**
 * A sprite atlas in one cave's colours (docs/spec/04 §3): every frame of every sprite, 16 x 16,
 * background transparent so a flash of the background colour shows through, as on the C64.
 */

import { makeCanvas } from './canvas';
import { KEY_COLOURS, rgb } from './palette';
import { SPRITE_NAMES, SPRITES } from './sprites';
import type { SpriteName, SpritePalette } from './sprites';
import { CELL, FRAME_H, FRAME_W } from './sprites/types';

const PER_ROW = 16;

/** Where each sprite's first frame sits in the atlas. */
const FIRST = new Map<SpriteName, number>();
let total = 0;
for (const name of SPRITE_NAMES) {
  FIRST.set(name, total);
  total += SPRITES[name].frames.length;
}

/** The three colours a frame's `1` `2` `3` stand for. */
function coloursFor(palette: SpritePalette, cave: readonly [number, number, number]): readonly number[] {
  if (palette === 'cave') return cave;
  const key = KEY_COLOURS[Number(palette.slice(3))] ?? 7;
  const dark = [8, 5, 2][Number(palette.slice(3))] ?? 11;
  return [dark, key, 1];
}

export class Atlas {
  readonly canvas: HTMLCanvasElement;
  readonly colors: readonly [number, number, number];

  constructor(colors: readonly [number, number, number]) {
    this.colors = colors;
    this.canvas = makeCanvas(PER_ROW * CELL, Math.ceil(total / PER_ROW) * CELL);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    const img = ctx.createImageData(this.canvas.width, this.canvas.height);
    for (const name of SPRITE_NAMES) {
      const sprite = SPRITES[name];
      const cols = coloursFor(sprite.palette, colors).map(rgb);
      sprite.frames.forEach((frame, f) => {
        const index = (FIRST.get(name) ?? 0) + f;
        const ox = (index % PER_ROW) * CELL;
        const oy = Math.floor(index / PER_ROW) * CELL;
        for (let y = 0; y < FRAME_H; y++) {
          for (let x = 0; x < FRAME_W; x++) {
            const ch = frame[y]?.[x] ?? '.';
            if (ch === '.') continue;
            const [r = 0, g = 0, b = 0] = cols[Number(ch) - 1] ?? [255, 0, 255];
            for (let dx = 0; dx < 2; dx++) img.data.set([r, g, b, 255], ((oy + y) * this.canvas.width + ox + x * 2 + dx) * 4);
          }
        }
      });
    }
    ctx.putImageData(img, 0, 0);
  }

  /** Draw frame `f` (wrapped) of `name` with its top-left at (x, y). */
  draw(ctx: CanvasRenderingContext2D, name: SpriteName, f: number, x: number, y: number): void {
    const n = SPRITES[name].frames.length;
    const index = (FIRST.get(name) ?? 0) + (((f % n) + n) % n);
    ctx.drawImage(this.canvas, (index % PER_ROW) * CELL, Math.floor(index / PER_ROW) * CELL, CELL, CELL, x, y, CELL, CELL);
  }
}

const cache = new Map<string, Atlas>();

/** Atlases are cached by colour scheme: a cave's is built once. */
export function atlasFor(colors: readonly [number, number, number]): Atlas {
  const key = colors.join(',');
  let atlas = cache.get(key);
  if (!atlas) {
    atlas = new Atlas(colors);
    cache.set(key, atlas);
  }
  return atlas;
}
